'use client';
import { useEffect, useRef, useState } from 'react';
import { classNames } from '@repo/ui/src/utils/classNames';
import {
  buildVidUrlMP4,
  buildVidUrlWebM,
  getPlaceholderImage,
} from '@repo/ui/src/functions/video-functions';
import { MediaType } from '@repo/ui/src/types/image';
import VideoPauseSmall from '@repo/ui/src/components/Icons/VideoPauseSmall';
import VideoPlaySmall from '@repo/ui/src/components/Icons/VideoPlaySmall';
import useVideoPlayback from '@repo/ui/src/hooks/useVideoPlayback';

interface Props {
  video: MediaType;
  width: number;
  fillContainer?: boolean;
  className?: string;
  playButton?: boolean;
  videoOverlay?: boolean;
  playButtonPosition?: 'left' | 'right';
}

const BackgroundVideo = ({
  video,
  width,
  fillContainer = false,
  className = '',
  playButton = true,
  videoOverlay,
  playButtonPosition = 'left',
}: Props) => {
  let speed = '3g';
  let vidWidth;
  let vidHeight;
  if (width && width <= 854 && !fillContainer) {
    vidWidth = 854;
    vidHeight = 480;
  } else if (width && width <= 1280 && !fillContainer) {
    vidWidth = 1280;
    vidHeight = 720;
  } else if (width && width >= 1280 && !fillContainer) {
    vidWidth = 1920;
    vidHeight = 1080;
  } else {
    vidWidth = '100%';
    vidHeight = 'auto';
  }
  if (typeof window !== 'undefined') {
    const connection =
      // @ts-expect-error connection is not defined on navigator
      navigator?.connection;
    speed = connection?.effectiveType || speed;
  }
  if (speed !== '2g' && width < 427) {
    speed = '3g';
  } else if (speed === '4g' && width < 854) {
    speed = '3g';
  }
  const thumbnail = getPlaceholderImage(video, vidWidth);
  const vidUrlMp4 = buildVidUrlMP4(video, vidWidth, speed);
  const vidUrlWebM = buildVidUrlWebM(video, vidWidth, speed);
  const videoRef = useRef<HTMLVideoElement>(null);
  const { play, pause } = useVideoPlayback(() => videoRef.current);
  const [playing, setPlaying] = useState(true);
  const toggle = () => setPlaying((curr) => !curr);

  useEffect(() => {
    if (playing) {
      play();
    } else {
      pause();
    }
  }, [playing, play, pause]);

  return (
    <div className={classNames('w-full bg-neutral-600', className)}>
      <div
        data-testid="video-container"
        className={classNames(
          'flex content-center items-center w-full overflow-hidden relative h-full',
          fillContainer && 'max-h-full',
          !fillContainer && (Number(vidHeight) > 720 ? 'max-h-[720px]' : 'max-h-[480px]'),
        )}
      >
        {playButton && (
          <button
            onClick={toggle}
            aria-label={playing ? 'pause video' : 'play video'}
            className={classNames(
              'absolute bottom-5 z-[1010] hover:text-brand-400',
              playButtonPosition === 'left' && 'left-10 md:left-5',
              playButtonPosition === 'right' && 'right-10 md:right-5',
            )}
          >
            {playing ? <VideoPauseSmall /> : <VideoPlaySmall />}
          </button>
        )}
        {videoOverlay && (
          <div
            data-testid="video-overlay"
            className="absolute top-0 bottom-0 right-0 left-0 w-full h-full bg-[rgba(0,0,0,0.55)]"
          />
        )}

        <video
          autoPlay
          loop
          muted
          poster={thumbnail}
          playsInline
          preload="auto"
          width={vidWidth}
          height={vidHeight}
          key={vidUrlMp4}
          ref={videoRef}
          data-testid="background-video"
          className={classNames(
            'object-cover h-full motion-reduce:hidden',
            fillContainer && 'min-h-[480px]',
          )}
        >
          <source src={vidUrlMp4} type="video/mp4" />
          <source src={vidUrlWebM} type="video/webm" />
        </video>
      </div>
    </div>
  );
};

export default BackgroundVideo;
